import { DialogController } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { StatisticsExportService, TableData, ChartData } from 'services/statistics-export-service';

export interface ExportItem {
  id: string;
  title: string;
  type: 'table' | 'chart';
  selected: boolean;
  data: TableData | ChartData;
}

export type ExportDialogModel = {
  tables: TableData[];
  charts?: ChartData[];
  dateRange?: { from: Date; to: Date };
  reportTitle?: string;
};

@autoinject
export class StatisticsExportDialog {
  protected customTitle: string = '';
  protected exportFormat: 'excel' | 'powerpoint' = 'excel';
  protected exporting: boolean = false;
  protected exportItems: ExportItem[] = [];

  private dateRange?: { from: Date; to: Date };

  constructor(
    protected controller: DialogController,
    protected t: I18N,
    private exportService: StatisticsExportService
  ) {}

  public activate(model: ExportDialogModel) {
    this.dateRange = model.dateRange;
    this.customTitle = model.reportTitle || this.t.tr('general.statistics') || 'Statistics';

    // Build selectable items from tables
    this.exportItems = [];

    model.tables.forEach((table, index) => {
      this.exportItems.push({
        id: `table-${index}`,
        title: table.title,
        type: 'table',
        selected: true,
        data: table,
      });
    });

    // Add charts as selectable items
    (model.charts || []).forEach((chart, index) => {
      this.exportItems.push({
        id: `chart-${index}`,
        title: chart.title,
        type: 'chart',
        selected: true,
        data: chart,
      });
    });
  }

  protected get tableItems(): ExportItem[] {
    return this.exportItems.filter((item) => item.type === 'table');
  }

  protected get chartItems(): ExportItem[] {
    return this.exportItems.filter((item) => item.type === 'chart');
  }

  protected get hasSelection(): boolean {
    return this.exportItems.some((item) => item.selected);
  }

  protected selectAll(): void {
    this.exportItems.forEach((item) => (item.selected = true));
  }

  protected deselectAll(): void {
    this.exportItems.forEach((item) => (item.selected = false));
  }

  protected async exportData(): Promise<void> {
    const selectedItems = this.exportItems.filter((item) => item.selected);
    if (selectedItems.length === 0) return;

    this.exporting = true;

    try {
      const selectedTables = selectedItems.filter((item) => item.type === 'table').map((item) => item.data as TableData);

      const selectedCharts = selectedItems.filter((item) => item.type === 'chart').map((item) => item.data as ChartData);

      if (this.exportFormat === 'excel') {
        // Charts are converted to tables for Excel export
        const chartTables = selectedCharts.map((chart) => this.chartToTable(chart));
        const allTables = [...selectedTables, ...chartTables];
        await this.exportService.exportToExcel(allTables, this.customTitle || 'Statistics_Export');
      } else {
        await this.exportService.exportToPowerPoint(selectedTables, 'custom', this.customTitle, this.dateRange, selectedCharts);
      }

      this.controller.ok('exported');
    } catch (err) {
      throw err;
    } finally {
      this.exporting = false;
    }
  }

  private chartToTable(chart: ChartData): TableData {
    const headers = ['', ...chart.categories];
    const rows = chart.series.map((s) => [s.name, ...s.values.map((v) => v ?? '--')]);
    return { title: chart.title, headers, rows };
  }
}
